import { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';
import './Announcements.css';

function EditAnnouncement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [documentName, setDocumentName] = useState('');

  const [form, setForm] = useState({
    title: '',
    body: '',
    is_general: false,
    visible_from: '',
    visible_until: '',
    status: 'active',

    image: null,
    document: null,

    current_image_url: '',
    current_document_url: '',
    current_document_name: '',

    remove_image: false,
    remove_document: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [groupsRes, announcementRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/announcements/groups`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/announcements/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const item = announcementRes.data.data || {};

        setGroups(groupsRes.data.data || []);
        setSelectedGroups((item.groups || []).map((g) => g.groep_id));

        setForm({
          title: item.title || '',
          body: item.body || '',
          is_general: Boolean(item.is_general),
          visible_from: item.visible_from ? item.visible_from.slice(0, 10) : '',
          visible_until: item.visible_until ? item.visible_until.slice(0, 10) : '',
          status: item.status || 'active',

          image: null,
          document: null,

          current_image_url: item.image_url || '',
          current_document_url: item.document_url || '',
          current_document_name: item.document_original_name || '',

          remove_image: false,
          remove_document: false,
        });

        setImagePreview(item.image_url || null);
        setDocumentName(item.document_original_name || '');
      } catch (error) {
        console.error('Fout bij ophalen mededeling:', error);

        await Swal.fire({
          icon: 'error',
          title: 'Mededeling ophalen mislukt',
          text: error.response?.data?.message || 'Probeer het opnieuw.',
        });

        navigate('/announcements');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, token, navigate]);

  const handleGroupToggle = (groepId) => {
    setSelectedGroups((prev) =>
      prev.includes(groepId)
        ? prev.filter((item) => item !== groepId)
        : [...prev, groepId]
    );
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;

    setForm((prev) => ({
      ...prev,
      image: file,
      remove_image: false,
    }));

    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview(form.current_image_url || null);
    }
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files?.[0] || null;

    setForm((prev) => ({
      ...prev,
      document: file,
      remove_document: false,
    }));

    setDocumentName(file?.name || form.current_document_name || '');
  };

  const handleRemoveImage = async () => {
    const result = await Swal.fire({
      title: 'Afbeelding verwijderen?',
      text: 'De huidige afbeelding wordt verwijderd na het opslaan.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, verwijderen',
      cancelButtonText: 'Annuleren',
      confirmButtonColor: '#dc3545',
    });

    if (!result.isConfirmed) return;

    setForm((prev) => ({
      ...prev,
      remove_image: true,
      image: null,
    }));

    setImagePreview(null);
  };

  const handleRemoveDocument = async () => {
    const result = await Swal.fire({
      title: 'Document verwijderen?',
      text: 'Het huidige document wordt verwijderd na het opslaan.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, verwijderen',
      cancelButtonText: 'Annuleren',
      confirmButtonColor: '#dc3545',
    });

    if (!result.isConfirmed) return;

    setForm((prev) => ({
      ...prev,
      remove_document: true,
      document: null,
    }));

    setDocumentName('');
  };

  const validateForm = async () => {
    if (!form.title.trim()) {
      await Swal.fire('Verplicht', 'Titel is verplicht.', 'warning');
      return false;
    }

    if (!form.visible_from) {
      await Swal.fire('Verplicht', 'Zichtbaar vanaf is verplicht.', 'warning');
      return false;
    }

    if (!form.visible_until) {
      await Swal.fire('Verplicht', 'Zichtbaar t/m is verplicht.', 'warning');
      return false;
    }

    if (form.visible_until < form.visible_from) {
      await Swal.fire(
        'Ongeldige periode',
        'De einddatum mag niet vóór de startdatum liggen.',
        'warning'
      );
      return false;
    }

    if (!form.is_general && selectedGroups.length === 0) {
      await Swal.fire(
        'Groepen ontbreken',
        'Kies minimaal één groep of selecteer algemeen voor iedereen.',
        'warning'
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) return;

    try {
      const payload = new FormData();

      payload.append('title', form.title.trim());
      payload.append('body', form.body || '');
      payload.append('is_general', String(form.is_general));
      payload.append('visible_from', form.visible_from);
      payload.append('visible_until', form.visible_until);
      payload.append('status', form.status);
      payload.append('groep_ids', JSON.stringify(selectedGroups));

      payload.append('remove_image', String(form.remove_image));
      payload.append('remove_document', String(form.remove_document));

      if (form.image && !form.remove_image) {
        payload.append('image', form.image);
      }

      if (form.document && !form.remove_document) {
        payload.append('document', form.document);
      }

      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/announcements/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      await Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: 'Mededeling bijgewerkt',
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });

      navigate('/announcements');
    } catch (error) {
      console.error('Fout bij bijwerken mededeling:', error);

      await Swal.fire({
        icon: 'error',
        title: 'Opslaan mislukt',
        text:
          error.response?.data?.message ||
          'Er is iets misgegaan bij het opslaan van de mededeling.',
      });
    }
  };

  if (loading) {
    return <div className="announcements-page">Mededeling laden...</div>;
  }

  return (
    <div className="announcements-page">
      <div className="bg-white p-4">
        <h2 className="text-center">Mededeling bewerken</h2>
        <hr className="seperator" />

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">
              Titel <span className="text-danger">*</span>
            </label>
            <input
              className="form-control rounded-0"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Berichttekst</label>
            <textarea
              className="form-control rounded-0"
              rows="5"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
          </div>

          <div className="row">
            <div className="mb-3 col-md-6">
              <label className="form-label">
                Zichtbaar vanaf <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control rounded-0"
                value={form.visible_from}
                onChange={(e) =>
                  setForm({ ...form, visible_from: e.target.value })
                }
                required
              />
            </div>

            <div className="mb-3 col-md-6">
              <label className="form-label">
                Zichtbaar t/m <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control rounded-0"
                value={form.visible_until}
                onChange={(e) =>
                  setForm({ ...form, visible_until: e.target.value })
                }
                required
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Status</label>
            <select
              className="form-select rounded-0"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="active">Actief</option>
              <option value="inactive">Inactief</option>
            </select>
          </div>

          <div className="mb-3 form-check">
            <input
              id="isGeneralAnnouncement"
              type="checkbox"
              className="form-check-input"
              checked={form.is_general}
              onChange={(e) =>
                setForm({ ...form, is_general: e.target.checked })
              }
            />
            <label className="form-check-label" htmlFor="isGeneralAnnouncement">
              Algemeen voor iedereen
            </label>
          </div>

          {!form.is_general && (
            <div className="mb-3">
              <label className="form-label">Koppel aan groepen</label>

              <div className="announcement-group-box">
                {groups.length === 0 ? (
                  <p className="text-muted mb-0">Geen groepen gevonden.</p>
                ) : (
                  groups.map((group) => (
                    <div key={group.groep_id} className="form-check">
                      <input
                        id={`group-${group.groep_id}`}
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedGroups.includes(group.groep_id)}
                        onChange={() => handleGroupToggle(group.groep_id)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`group-${group.groep_id}`}
                      >
                        {group.groep_name}
                        {group.school_year ? ` — ${group.school_year}` : ''}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="form-label">Afbeelding</label>

            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="form-control rounded-0"
              onChange={handleImageChange}
              disabled={form.remove_image}
            />

            {imagePreview && !form.remove_image && (
              <div className="announcement-image-preview mt-3">
                <img src={imagePreview} alt="Voorbeeld" />
              </div>
            )}

            {form.current_image_url && !form.remove_image && (
              <div className="mt-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                <a
                  href={form.current_image_url}
                  target="_blank"
                  rel="noreferrer"
                  className="announcement-file-link"
                >
                  Huidige afbeelding bekijken
                </a>

                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleRemoveImage}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Verwijder afbeelding
                </button>
              </div>
            )}

            {form.remove_image && (
              <div className="alert alert-warning mt-2 py-2">
                Afbeelding wordt verwijderd na opslaan.
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="form-label">Document</label>

            <input
              type="file"
              accept=".pdf,.doc,.docx"
              className="form-control rounded-0"
              onChange={handleDocumentChange}
              disabled={form.remove_document}
            />

            {documentName && !form.remove_document && (
              <div className="mt-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                {form.current_document_url && !form.document ? (
                  <a
                    href={form.current_document_url}
                    target="_blank"
                    rel="noreferrer"
                    className="announcement-file-link"
                  >
                    <i className="bi bi-paperclip me-1"></i>
                    {documentName}
                  </a>
                ) : (
                  <span className="announcement-file-link">
                    <i className="bi bi-paperclip me-1"></i>
                    {documentName}
                  </span>
                )}

                {form.current_document_url && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleRemoveDocument}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Verwijder document
                  </button>
                )}
              </div>
            )}

            {form.remove_document && (
              <div className="alert alert-warning mt-2 py-2">
                Document wordt verwijderd na opslaan.
              </div>
            )}
          </div>

          <hr className="seperator" />

          <div className="row">
            <div className="col-md-6 mb-2">
              <button
                type="button"
                className="btn btn-secondary w-100 rounded-0"
                onClick={() => navigate('/announcements')}
              >
                Annuleren
              </button>
            </div>

            <div className="col-md-6 mb-2">
              <button type="submit" className="btn btn-primary w-100 rounded-0">
                Opslaan
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditAnnouncement;